<template>
  <Icon 
    :name="iconName" 
    :size="size"
    :class="iconClass"
    :aria-label="ariaLabel"
    role="img"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Icon from './Icon.vue';
import type { NotificationSeverity } from '@/types/notifications';

export interface NotificationIconProps {
  severity: NotificationSeverity;
  size?: number;
}

const props = withDefaults(defineProps<NotificationIconProps>(), {
  size: 16,
});

const iconName = computed(() => {
  switch (props.severity) {
    case 'success':
      return 'check';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    case 'info':
    default:
      return 'info';
  }
});

const iconClass = computed(() => [
  'notification-icon',
  `notification-icon--${props.severity}`
]);

const ariaLabel = computed(() => {
  switch (props.severity) {
    case 'success':
      return 'Success';
    case 'warning':
      return 'Warning';
    case 'error':
      return 'Error';
    case 'info':
    default:
      return 'Information';
  }
});
</script>

<style scoped lang="scss">
.notification-icon {
  flex-shrink: 0;
  display: inline-block; // Ensure it's visible
  
  &--success {
    color: var(--vscode-notificationsInfoIcon-foreground, #00aa00);
  }
  
  &--warning {
    color: var(--vscode-notificationsWarningIcon-foreground, #ff8c00);
  }
  
  &--error {
    color: var(--vscode-notificationsErrorIcon-foreground, #f14c4c);
  }
  
  &--info {
    color: var(--vscode-notificationsInfoIcon-foreground, #007acc);
  }
}
</style>
